import { z } from "zod";
import { I18nText } from "./common.ts";

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const MonthDay = z
  .string()
  .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, "MM-DD")
  .refine(
    (md) => Number(md.slice(3)) <= DAYS_IN_MONTH[Number(md.slice(0, 2)) - 1]!,
    "no such date",
  );

/**
 * Recurring yearly closure, e.g. a spawning-season dive ban.
 * Inclusive on both ends. `from > to` wraps the new year (e.g. 11-15 → 02-28).
 */
export const ClosedPeriod = z
  .object({ from: MonthDay, to: MonthDay, reason: I18nText.optional() })
  .strict();
export type ClosedPeriod = z.infer<typeof ClosedPeriod>;

/** Calendar date YYYY-MM-DD in Europe/Zurich. Both sides use it for "today" (e.g. sightings). */
export function zurichDate(date: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Uses the date's calendar day in Europe/Zurich, not UTC. */
export function isClosedOn(periods: readonly ClosedPeriod[], date: Date): ClosedPeriod | null {
  if (Number.isNaN(date.getTime())) return null;
  const md = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    month: "2-digit",
    day: "2-digit",
  })
    .format(date)
    .replace("/", "-");
  for (const p of periods) {
    const inside = p.from <= p.to ? md >= p.from && md <= p.to : md >= p.from || md <= p.to;
    if (inside) return p;
  }
  return null;
}
